// Editing a toggle (`<details>`). The data model is untouched: the node
// (content model, `open` attribute), its markdown and `persist: true` stay in
// the editor package; only the node views (the fold button) are added here.
//
// Upstream's node view cannot be used as is. `@tiptap/extension-details`
// builds
//
//     <div data-type="details" data-block-id="…" class="is-open?">
//       <button>            <- the fold button
//       <div>               <- contentDOM
//         <summary>…</summary>
//         <div data-type="detailsContent" hidden>…</div>
//
// and with `persist: true` writes `open` back with
// `tr.setNodeMarkup(pos, undefined, { open: !currentNode.attrs.open })` -
// replacing all attributes. The block id falls to null, the block id plugin
// assigns a new one, and one open/close leaves the document with a new id and
// the DOM with the old one (the node view's `update()` does not re-apply
// attributes). Threads anchored to the block would be orphaned, and a check
// by `data-block-id` would think a gone id is alive.
//
// So the node view is rewritten on the same skeleton, fixing two things:
//   (a) `setNodeMarkup` gets `{ ...currentNode.attrs, open }`, keeping the id;
//   (b) `update()` re-applies `data-block-id` / `open` to the DOM, so other
//       paths changing attributes (the id plugin resolving duplicates) never
//       leave the DOM and the document apart.
import { mergeAttributes, type NodeViewRendererProps } from "@tiptap/core";
import { BLOCK_ID_DOM_ATTR, Details, DetailsSummary } from "@trameria/editor";
import { m } from "@trameria/i18n";
import { canEdit } from "./editor-steps";

const DETAILS_NAME = "details";
const SUMMARY_NAME = "detailsSummary";

/**
 * The DOM event (bubbling) the summary's button sends to its details node view
 * ("toggle"). The button sits in the summary's node view, right after the
 * title; the details view is an ancestor, so it is not touched directly.
 */
export const TOGGLE_DETAILS_EVENT = "trameria-toggle-details";

/** The summary text's holder (contentDOM). */
export const DETAILS_SUMMARY_TEXT_CLASS = "trameria-details-summary-text";

/** The fold button (look in the CSS). */
export const DETAILS_TOGGLE_CLASS = "trameria-details-toggle";

/**
 * Draws the fold button; called on every toggle (and the first draw), so
 * state-dependent attributes are written every time. No `title`: tooltips are
 * not shown.
 */
export function renderDetailsToggleButton({
  element,
  isOpen,
}: {
  element: HTMLButtonElement;
  isOpen: boolean;
}): void {
  element.className = DETAILS_TOGGLE_CLASS;
  element.setAttribute("aria-expanded", isOpen ? "true" : "false");
  element.setAttribute(
    "aria-label",
    isOpen ? m.blocks_toggle_close() : m.blocks_toggle_open(),
  );
  // The glyph is the CSS's `::before`, not text: the button is inside the
  // summary, and a "▸" in textContent would leak into the summary's text
  // (copies, tests). One glyph, rotated when open (two glyphs differ in width
  // and would shift what follows).
  element.textContent = "";
}

interface DetailsNodeLike {
  type: NodeViewRendererProps["node"]["type"];
  attrs: Record<string, unknown>;
}

/**
 * (b): re-applies the document's attributes to the DOM. `update()` gets no new
 * `HTMLAttributes`, so the two the DOM needs (`data-block-id`, `open`) are
 * made from the node's attributes.
 */
function syncDetailsDomAttributes(dom: HTMLElement, node: DetailsNodeLike) {
  const blockId = node.attrs.blockId;
  if (typeof blockId === "string" && blockId.length > 0) {
    dom.setAttribute(BLOCK_ID_DOM_ATTR, blockId);
  } else {
    // No empty `data-block-id` for selectors to hit (as `blockIdDomAttrs`).
    dom.removeAttribute(BLOCK_ID_DOM_ATTR);
  }
  if (node.attrs.open) dom.setAttribute("open", "");
  else dom.removeAttribute("open");
}

/**
 * The fold button goes right after the summary's text (decided: not on the
 * left, not at the far right). The summary's content is ProseMirror's
 * contentDOM, so a button added to a plain `<summary>` would be read back into
 * the text. The summary gets its own node view:
 *
 *     <summary>
 *       <span class="trameria-details-summary-text">…</span>   <- contentDOM
 *       <button class="trameria-details-toggle"></button>
 *
 * Pressing it signals the details view (which owns the hidden content) and,
 * when writable, writes `open` back.
 */
export const DetailsSummaryWithToggle = DetailsSummary.extend({
  addNodeView() {
    return ({ editor, getPos }) => {
      const dom = document.createElement("summary");
      const text = document.createElement("span");
      text.className = DETAILS_SUMMARY_TEXT_CLASS;
      const toggle = document.createElement("button");
      toggle.type = "button";
      toggle.contentEditable = "false";
      dom.append(text, toggle);

      /** The parent details (the summary is its first child: the summary's position - 1). */
      const parentDetails = () => {
        const pos = typeof getPos === "function" ? getPos() : undefined;
        if (typeof pos !== "number") return null;
        const $pos = editor.state.doc.resolve(pos);
        const parent = $pos.parent;
        if (parent.type.name !== DETAILS_NAME) return null;
        return { pos: $pos.before(), node: parent };
      };
      renderDetailsToggleButton({
        element: toggle,
        isOpen: Boolean(parentDetails()?.node.attrs.open),
      });

      // Pressing must not move the body's selection and focus.
      toggle.addEventListener("mousedown", (e) => e.preventDefault());
      toggle.addEventListener("click", () => {
        // The look changes at once (the details view flips the hidden content
        // and redraws this button).
        toggle.dispatchEvent(
          new Event(TOGGLE_DETAILS_EVENT, { bubbles: true }),
        );
        // While reading on a phone (not writable for now), folding still works.
        if (!canEdit(editor)) return;
        const target = parentDetails();
        if (!target) return;
        const { from, to } = editor.state.selection;
        editor
          .chain()
          .command(({ tr }) => {
            const currentNode = tr.doc.nodeAt(target.pos);
            if (currentNode?.type.name !== DETAILS_NAME) return false;
            // (a): keep the other attributes, change only `open`.
            tr.setNodeMarkup(target.pos, undefined, {
              ...currentNode.attrs,
              open: !currentNode.attrs.open,
            });
            return true;
          })
          // The caret goes back where it was, then the focus (as upstream).
          .setTextSelection({ from, to })
          .focus(undefined, { scrollIntoView: false })
          .run();
      });

      return {
        dom,
        contentDOM: text,
        ignoreMutation(mutation) {
          if (mutation.type === "selection") return false;
          return toggle.contains(mutation.target) || mutation.target === dom;
        },
        stopEvent: (event) =>
          event.target instanceof Node && toggle.contains(event.target),
        update: (updatedNode) => updatedNode.type.name === SUMMARY_NAME,
      };
    };
  },
});

/**
 * The details node with the fold behaviour. Name, content model, attributes
 * and markdown are the editor's `Details`. The button itself is in the summary
 * (above); this view holds the open state (`is-open` and the content's hidden)
 * and redraws the button.
 */
export const DetailsWithToggle = Details.extend({
  addNodeView() {
    return ({ node, HTMLAttributes }) => {
      const dom = document.createElement("div");
      const attributes = mergeAttributes(
        this.options.HTMLAttributes,
        HTMLAttributes,
        { "data-type": this.name },
      );
      for (const [key, value] of Object.entries(attributes)) {
        dom.setAttribute(key, value);
      }

      const content = document.createElement("div");
      dom.append(content);

      const { openClassName } = this.options;
      /** The button in the summary (absent until the child view exists). */
      const toggleButton = () =>
        content.querySelector<HTMLButtonElement>(
          `:scope > summary > .${DETAILS_TOGGLE_CLASS}`,
        );
      const redrawButton = (isOpen: boolean) => {
        const element = toggleButton();
        if (!element) return;
        renderDetailsToggleButton({ element, isOpen });
      };

      /**
       * Shows or hides the content (`setToValue` omitted: flips). The
       * `detailsContent` view (upstream's) does the hiding; this only signals
       * it with `toggleDetailsContent`.
       */
      const toggleDetailsContent = (setToValue?: boolean) => {
        if (setToValue !== undefined) {
          // Already so: nothing (no double flip of `hidden`).
          if (setToValue === dom.classList.contains(openClassName)) return;
          dom.classList.toggle(openClassName, setToValue);
        } else {
          dom.classList.toggle(openClassName);
        }
        redrawButton(dom.classList.contains(openClassName));
        content
          .querySelector(':scope > div[data-type="detailsContent"]')
          ?.dispatchEvent(new Event("toggleDetailsContent"));
      };

      // `detailsContent`'s view is always born hidden, so an open node is
      // opened in the next task (after the child views exist).
      if (node.attrs.open) setTimeout(() => toggleDetailsContent());

      // The summary button's signal (this details' own only: an inner
      // toggle's details takes and stops it first).
      dom.addEventListener(TOGGLE_DETAILS_EVENT, (event) => {
        event.stopPropagation();
        toggleDetailsContent();
      });

      return {
        dom,
        contentDOM: content,
        ignoreMutation(mutation) {
          if (mutation.type === "selection") return false;
          const target = mutation.target;
          return !dom.contains(target) || dom === target;
        },
        update: (updatedNode) => {
          if (updatedNode.type !== this.type) return false;
          // (b): attributes change by other paths too (the id plugin's
          // duplicate resolution, normalisation); re-applied every time.
          syncDetailsDomAttributes(dom, updatedNode);
          toggleDetailsContent(Boolean(updatedNode.attrs.open));
          return true;
        },
      };
    };
  },
});
